import React, { useRef, useState } from 'react';

import { Alert, Button, SpaceBetween } from '@amzn/awsui-components-react';

import { OnboardingStatus } from '../../constants/onboardingStatus';
import { EventName, EventType, logMetricEvent } from '../../services/eventService';
import updateDeviceComponentStatusHandler, {
    UpdateDeviceComponentStatusRequest,
} from '../../services/updateDeviceComponentStatusHandler';
import { ComponentType } from './componentType';

interface NetradyneDissociationProps {
    deviceId: string;
    vin: string;
    componentType: ComponentType;
    serialNumber: string;
    alreadyRequested?: boolean;
    onRequested?: () => void;
}

const NetradyneDissociation = ({
    deviceId,
    vin,
    componentType,
    serialNumber,
    alreadyRequested,
    onRequested,
}: NetradyneDissociationProps) => {
    const [isRequested, setIsRequested] = useState(!!alreadyRequested);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Scoped to this action so a dissociation failure cannot overwrite an install message, or be overwritten by one.
    const [hasError, setHasError] = useState(false);
    const startTime = useRef(0);

    // Duration is passed only on an outcome: on START it would read as a step that finished instantly.
    const logDissociationEvent = (
        eventType: EventType,
        durationMs?: number,
        miscEventData?: Record<string, unknown>,
    ) => {
        logMetricEvent({
            deviceId,
            vin,
            eventName: EventName.NETRADYNE_DISSOCIATION,
            eventType,
            componentType: componentType.value,
            componentId: serialNumber,
            duration_ms: durationMs,
            miscEventData,
        });
    };

    const handleDissociate = () => {
        startTime.current = Date.now();
        setIsSubmitting(true);
        setHasError(false);
        logDissociationEvent(EventType.START);

        // No notes: the DAO skips null fields, so omitting them leaves the install notes intact.
        const updateRequest: UpdateDeviceComponentStatusRequest = {
            deviceId,
            componentId: serialNumber,
            vin,
            componentType: componentType.value,
            status: OnboardingStatus.READY_FOR_NETRADYNE_DISSOCIATION,
        };

        updateDeviceComponentStatusHandler(updateRequest, (apiSuccess: boolean, result: unknown) => {
            setIsSubmitting(false);
            const durationMs = Date.now() - startTime.current;

            if (apiSuccess) {
                logDissociationEvent(EventType.SUCCESS, durationMs);
                setIsRequested(true);
                onRequested?.();
                return;
            }

            setHasError(true);
            // Never forward the raw failure: it is the axios error, whose config.headers carries the session JWT.
            const apiError = result as { message?: string; response?: { status?: number } };
            logDissociationEvent(EventType.FAILURE, durationMs, {
                error: 'Failed to update component status to ReadyForNetradyneDissociation',
                apiErrorMessage: apiError?.message,
                apiErrorStatus: apiError?.response?.status,
            });
        });
    };

    if (isRequested) {
        return (
            <Alert type="warning" header="Netradyne Dissociation Requested">
                The Netradyne camera on VIN {vin} has been submitted for dissociation.
            </Alert>
        );
    }

    return (
        <SpaceBetween size="s">
            {hasError && (
                <Alert type="error" header="Dissociate Netradyne camera">
                    The request could not be submitted. Please try again.
                </Alert>
            )}

            <Button variant="primary" onClick={handleDissociate} loading={isSubmitting} disabled={isSubmitting}>
                Dissociate Netradyne Camera
            </Button>
        </SpaceBetween>
    );
};

export default NetradyneDissociation;
